/**
 * The two money surfaces that aren't the budget itself: Insights and Insurance.
 *
 * Rendered under the category cards on the Budget page, because that page is where
 * Brian actually lands and neither of these had a way in. Insights was reachable only
 * from a link buried on this page; Insurance had no entry point at all.
 *
 * Each tile carries a live number rather than being a bare link — a dashboard tile that
 * says nothing is just a nav item taking up more room than a nav item needs.
 *
 * Self-contained fetching on purpose: BudgetPage's own loader stays untouched, and a
 * failure here degrades to a plain link instead of taking the budget down with it.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, currentPeriod } from '../../lib/api';
import type { HealthScore, InsuranceQuote } from '../../shared/types';
import { Card } from './ui';

interface QuoteSummary {
  live: number;
  waiting: number;
  closed: number;
  lines: string[];
}

function summarize(quotes: InsuranceQuote[]): QuoteSummary {
  const live = quotes.filter((q) => q.status === 'received' && !q.is_expired);
  const waiting = quotes.filter((q) => q.status === 'requested');
  const closed = quotes.filter(
    (q) => q.status === 'declined' || q.status === 'expired' || q.status === 'bound'
  );
  // Which lines are actually being shopped. Deliberately NOT "which lines lack a quote" —
  // auto and umbrella have no quotes because they are in force and nobody is shopping them.
  // Framing an absent quote as a gap would be alarming and wrong.
  const lines = [...new Set([...live, ...waiting].map((q) => q.policy_type))];
  return { live: live.length, waiting: waiting.length, closed: closed.length, lines };
}

export default function MoneyExtras() {
  const [health, setHealth] = useState<HealthScore | null>(null);
  const [quotes, setQuotes] = useState<QuoteSummary | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Both are best-effort. The health endpoint 404s by design when a month has nothing
    // to score, and insurance 404s on a server whose Worker predates the module — neither
    // is an error worth showing here, so each tile just falls back to its plain label.
    api
      .get<HealthScore>(`/insights/health?period=${currentPeriod()}`)
      .then((h) => !cancelled && setHealth(h))
      .catch(() => {});

    api
      .get<InsuranceQuote[]>('/insurance/quotes')
      .then((q) => !cancelled && setQuotes(summarize(q)))
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <h2 className="mb-3 font-display text-lg font-semibold text-ink">Also in Money</h2>

      <div className="grid gap-4 sm:grid-cols-2">
        <Tile
          to="/money/insights"
          title="Insights"
          headline={
            health ? `Health score ${health.score} of 100` : 'Health score, once the month has data'
          }
          body="How the month is really going, what keeps charging us, what we're saving for, and the rules we set."
          cta="Open insights"
        />

        <Tile
          to="/money/insurance"
          title="Insurance"
          headline={insuranceHeadline(quotes)}
          body="Every quote requested and received, scored against the coverage this house actually needs."
          cta="Open the shopping board"
        />
      </div>
    </div>
  );
}

function insuranceHeadline(q: QuoteSummary | null): string {
  if (!q) return 'Quotes, scored against your checklist';
  if (q.live > 0) {
    return `${q.live} live quote${q.live === 1 ? '' : 's'} on the table`;
  }
  if (q.waiting > 0) {
    return `${q.waiting} request${q.waiting === 1 ? '' : 's'} still out`;
  }
  if (q.closed > 0) {
    return `No live quotes · ${q.closed} closed`;
  }
  return 'Nothing on the board yet';
}

function Tile({
  to,
  title,
  headline,
  body,
  cta,
}: {
  to: string;
  title: string;
  headline: string;
  body: string;
  cta: string;
}) {
  return (
    <Card>
      {/* The whole card is one link, so the tap target is the card and not just the text.
          The heading carries the accessible name; the CTA is decorative alongside it. */}
      <Link
        to={to}
        className="-m-1 block rounded-lg p-1 focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-canvas focus-visible:outline-none"
      >
        <h3 className="font-display text-base font-semibold text-ink">{title}</h3>
        <p className="mt-1 text-sm font-medium text-ink">{headline}</p>
        <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">{body}</p>
        <span className="mt-3 inline-block text-sm font-medium text-sage underline underline-offset-2">
          {cta}
        </span>
      </Link>
    </Card>
  );
}
